import type { AlhenaChatConfig } from '@integration-layer/ampliance/schemas/alhena-chat-config-schema.localized'
import { resetAlhenaChatTracking } from '@integration-layer/composables/useAlhenaChatTracking'
import { resolveBrandDeliveryKey } from '@integration-layer/utils/resolveBrandDeliveryKey'

const DEFAULT_API_BASE_URL = 'https://app.alhena.ai'
const DEFAULT_SCRIPT_URL = 'https://app.alhena.ai/sdk/gleenWidget.js'

const getAlhenaCompanyId = (isProduction: boolean) =>
  isProduction ? 'armani_global_prod' : 'armani_global_staging'

let headEntry: ReturnType<typeof useHead> | null = null
// Only the latest init() may apply its config, so a late global response can't override the brand's.
let latestInitId = 0

/**
 * Loads the Alhena chat widget from Amplience CMS configuration.
 * Fetches brand/locale-specific config and injects SDK scripts when enabled.
 */
export function useAlhenaChat() {
  const localePath = useLocalePath()
  const route = useRoute()
  const { currentWebsite } = useWebsite()
  const isAlhenaChatEnabled = useState('alhena-chat-enabled', () => false)
  // Whether init() has finished deciding. The flag above starts false, so before
  // this turns true "chat is off" and "we do not know yet" look identical, and
  // anything filtering on availability would show a first paint it has to redo.
  const isAlhenaChatResolved = useState('alhena-chat-resolved', () => false)

  const shouldLoadWidget = (config: AlhenaChatConfig | null) =>
    !!config?.enabled && !config.hideInCountry

  // Resolves false when a newer call superseded this one and nothing was applied.
  const init = async (): Promise<boolean> => {
    if (!import.meta.client) return true

    const initId = ++latestInitId

    const deliveryKey = resolveBrandDeliveryKey(
      'alhena-chat-config',
      currentWebsite.value
    )

    const config = await $fetch<AlhenaChatConfig | null>(
      '/api/cms/getAlhenaChatConfig',
      {
        query: {
          deliveryKey,
          locale: localePath(''),
          vse: route.query.vse,
          contentId: route.query.contentId,
        },
      }
    ).catch(() => {
      console.error('Alhena chat config fetch failed')
      return null
    })

    if (initId !== latestInitId) return false

    if (!shouldLoadWidget(config)) {
      isAlhenaChatEnabled.value = false

      resetAlhenaChatTracking()

      // Cleanup del widget
      if (window.gleenWidget) {
        window.gleenWidget.unload?.()
        delete window.gleenWidget
      }

      // 3. Cleanup tag <head>
      if (headEntry) {
        headEntry.dispose()
        headEntry = null
      }
      return true
    }

    isAlhenaChatEnabled.value = true

    if (window.gleenWidget) return true

    const { isProduction } = useRuntimeConfig().public
    const apiBaseUrl = config!.apiBaseUrl || DEFAULT_API_BASE_URL
    const scriptUrl = config!.scriptUrl || DEFAULT_SCRIPT_URL
    const company = getAlhenaCompanyId(!!isProduction)

    if (headEntry) headEntry.dispose()

    document.gleenConfig = {
      company,
      apiBaseUrl,
      // The widget watches these nodes and shifts the launcher (and the open
      // chat) above whatever sits in the lower half of the viewport.
      // `#navigation` is the mobile bar; `#pdp-ecommerce-sticky-bar` is the
      // desktop PDP bar, mounted only while visible.
      styles: {
        launcher_avoid_selector: '#navigation, #pdp-ecommerce-sticky-bar',
      },
    }

    // script injection
    headEntry = useHead({
      script: [
        {
          src: scriptUrl,
          async: true,
          tagPosition: 'bodyClose',
        },
      ],
    })

    return true
  }

  return { init, isAlhenaChatEnabled, isAlhenaChatResolved }
}
